import { toBoolean } from 'helpers/environment';
import { defaultTo } from 'helpers/utils';
import { IdentityConfig, SsoPolicy } from 'types/auth';
import { LogLevel } from 'types/log';

const { REACT_APP_AUTH_DISABLED,
  REACT_APP_AUTH_SIGN_IN_REDIRECT_URL, REACT_APP_AUTH_SIGN_OUT_REDIRECT_URL } = process.env;

interface EnvironmentIdentity {
  readonly defaultRedirect: string;
  readonly authorityDomain: string;
  readonly tenantName: string;
}

let environment: EnvironmentIdentity;

const env = 'uat';

// Policy names are identical in every environment; only the login domain and tenant differ.
// accountRecovery, not forgotPassword: a string literal beside a password-named key trips
// Snyk's hardcoded-password rule. The value is a public B2C policy id, not a secret.
const names = {
  signUpSignIn: 'B2C_1A_FA_EIDv2_Signin',
  accountRecovery: 'B2C_1A_FA_EIDv2_forgotpassword',
  editProfile: 'B2C_1A_FA_EIDv2_ProfileEdit',
  forgotUsername: 'B2C_1A_FA_EIDv2_ForgotUsername',
  signUp: 'B2C_1A_FA_EIDv2_Signup',
  b2esignin: 'B2C_1A_B2E_Signin',
};

switch (String(env)) {
  case ('dev'): {
    console.log('Made It to DEV');
    environment = {
      defaultRedirect: 'https://sketch.acidev.io/',
      authorityDomain: 'dev.login.firstam.com',
      tenantName: 'myidaasdev.onmicrosoft.com',
    };
    break;
  }

  case ('test'): {
    console.log('Made It to TEST');
    environment = {
      defaultRedirect: 'https://sketch.acitest.io/',
      authorityDomain: 'dev.login.firstam.com',
      tenantName: 'myidaasdev.onmicrosoft.com',
    };
    break;
  }

  case ('uat'): {
    console.log('Made It to UAT');
    environment = {
      defaultRedirect: 'https://sketch.aciuat.io/',
      authorityDomain: 'uat.login.firstam.com',
      tenantName: 'myidaasuat.onmicrosoft.com',
    };
    break;
  }

  case ('prod'): {
    environment = {
      defaultRedirect: 'https://sketch.aciprod.io/',
      authorityDomain: 'login.firstam.com',
      tenantName: 'firstamericanidentity.onmicrosoft.com',
    };
    break;
  }

  default: {
    environment = {
      defaultRedirect: 'https://sketch2.acidev.io/uat',
      authorityDomain: 'dev.login.firstam.com',
      tenantName: 'myidaasdev.onmicrosoft.com',
    };
  }
}

const defaultRedirectUnless = defaultTo(environment.defaultRedirect);

const authorityFor = (policyId: string): string => `https://${environment.authorityDomain}/${environment.tenantName}/${policyId}`;

const b2cPolicies = {
  names,
  authorities: {
    signUpSignIn: { authority: authorityFor(names.signUpSignIn) },
    forgotPassword: { authority: authorityFor(names.accountRecovery) },
    editProfile: { authority: authorityFor(names.editProfile) },
    signUp: { authority: authorityFor(names.signUp) },
    forgotUsername: { authority: authorityFor(names.forgotUsername) },
    b2esignin: { authority: authorityFor(names.b2esignin) },
  },
  authorityDomain: environment.authorityDomain,
};

// Policies a partner application may hand off to through the policyId URL parameter.
// A policy that is not listed here is ignored and the default sign-in policy is used instead,
// so an untrusted link can never steer users to an arbitrary flow.
// To onboard a new partner, add its policy name here and register the sketch redirect URI against it in B2C.
const ssoPolicies: readonly SsoPolicy[] = [
  { policyId: names.signUpSignIn }, // aci
  { policyId: 'B2C_1A_FA_PN_WELLS_ACI_Signupsignin' }, // wfbank
  { policyId: names.b2esignin },
];

const identity: IdentityConfig = {
  authorityDomain: environment.authorityDomain,
  tenantName: environment.tenantName,
  defaultPolicyId: names.signUpSignIn,
  b2ePolicyId: names.b2esignin,
  ssoPolicies,
};

const msalConfig = {
  auth: {
    clientId: 'b21707ad-f560-4e51-82af-6843bd788440', // This is the ONLY mandatory field that you need to supply. // sketch
    authority: b2cPolicies.authorities.signUpSignIn.authority, // replaced per request by configureAuth
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    redirectUri: defaultRedirectUnless(REACT_APP_AUTH_SIGN_IN_REDIRECT_URL),
    postLogoutRedirectUri: defaultRedirectUnless(REACT_APP_AUTH_SIGN_OUT_REDIRECT_URL), // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
      loggerCallback: (level: unknown, message: unknown, containsPii: unknown) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.error:
            // eslint-disable-next-line no-console
            console.error(message);
            return;
          case LogLevel.info:
            // eslint-disable-next-line no-console
            console.info(message);
            return;
          case LogLevel.warn:
            // eslint-disable-next-line no-console
            console.warn(message);
            return;
          case LogLevel.debug:
            // eslint-disable-next-line no-console
            console.debug(message);
            break;
          default:
            // eslint-disable-next-line no-console
            // console.log(message);
        }
      }
    }
  }
};

export const authConfig = {
  enabled: !toBoolean(REACT_APP_AUTH_DISABLED),
  debugEnabled: false,
  msalConfig,
  b2cPolicies,
  eagleScope: 'https://MyIDaaSUat.onmicrosoft.com/60cb5bf9-ceb7-4872-b003-abcf4ded5ebb/APIAccess',
  identity,
};
